import { useEffect, useState } from "react";
import { SEO } from "@/components/SEO";
import { Layout } from "@/components/Layout";
import { CreatorProfileModal } from "@/components/CreatorProfileModal";
import { TrackingInfoModal } from "@/components/TrackingInfoModal";
import { GameChatModal } from "@/components/GameChatModal";
import { KeyRequestsList } from "@/components/KeyRequestsList";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { addReport, removeReport, hasDeveloperReportedCreator, getCreatorReportDetails, formatReportDate, getCreatorTrustScore, getTrustScoreEmoji, getTrustScoreColor, getTrustScoreLabel } from "@/lib/reportUtils";
import { recalculateCreatorBadges } from "@/services/pointsService";
import { AccountNotVerified } from "@/components/AccountNotVerified";

interface KeyRequest {
  id: string;
  game_id: string;
  creator_id: string;
  message: string | null;
  status: 'pending' | 'approved' | 'rejected';
  response_message: string | null;
  is_covered: boolean;
  created_at: string;
  game: {
    id: string;
    title: string;
    cover_image_url: string | null;
  };
  creator: {
    id: string;
    display_name: string;
    avatar_url: string | null;
    platform: string | null;
    subscriber_count: number | null;
    bio: string | null;
    channel_url: string | null;
    favorite_genres: string | null;
    twitch_url: string | null;
    youtube_url: string | null;
    tiktok_url: string | null;
    instagram_url: string | null;
    twitter_url: string | null;
    bluesky_url: string | null;
    has_reported?: boolean;
    report_details?: {
      total_reports: number;
      latest_report_date: string | null;
    };
    trust_score?: number;
  };
}

export default function KeyRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState<KeyRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState<string | null>(null);
  const [isViewingCreatorProfile, setIsViewingCreatorProfile] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<any>(null);
  const [selectedGameForTracking, setSelectedGameForTracking] = useState<any>(null);
  const [isViewingTrackingInfo, setIsViewingTrackingInfo] = useState(false);
  const [isViewingChat, setIsViewingChat] = useState(false);
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [isCheckingVerification, setIsCheckingVerification] = useState(true);

  // Get game filter from URL
  const gameIdFromUrl = searchParams.get('game');

  useEffect(() => {
    fetchUserType();
  }, [user?.id]);

  useEffect(() => {
    if (userType === 'developer' && user?.id) {
      checkVerificationStatus();
    } else {
      setIsCheckingVerification(false);
      setIsVerified(true); // Non-developers don't need verification
    }
  }, [userType, user?.id]);

  useEffect(() => {
    if (userType && user?.id) {
      fetchRequests();
    }
  }, [user?.id, userType]);

  const fetchUserType = async () => {
    if (user) {
      const { data } = await supabase
        .from("profiles")
        .select("user_type")
        .eq("id", user.id)
        .single();

      setUserType(data?.user_type || null);
    }
  };

  const checkVerificationStatus = async () => {
    if (!user?.id || userType !== 'developer') return;

    setIsCheckingVerification(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("is_verified")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Error checking verification status:", error);
        setIsVerified(false);
      } else {
        setIsVerified(data?.is_verified === true);
      }
    } catch (error) {
      console.error("Error checking verification status:", error);
      setIsVerified(false);
    } finally {
      setIsCheckingVerification(false);
    }
  };

  const fetchRequests = async () => {
    if (!user) return;

    setLoading(true);
    try {
      let query = supabase
        .from("key_requests")
        .select(`
          id,
          game_id,
          creator_id,
          message,
          status,
          response_message,
          is_covered,
          created_at,
          game:games(
            id, 
            title, 
            cover_image_url
          ),
          creator:profiles!key_requests_creator_id_fkey(
            id, 
            display_name, 
            avatar_url, 
            platform, 
            subscriber_count,
            bio,
            channel_url,
            favorite_genres,
            twitch_url,
            youtube_url,
            tiktok_url,
            instagram_url,
            twitter_url,
            bluesky_url,
            steam_url,
            steam_id,
            is_verified,
            twitch_followers_count,
            twitch_subscribers_count,
            twitch_views_count,
            youtube_subscribers_count,
            youtube_views_count
          )
        `)
        .order("created_at", { ascending: false });


      const { data, error } = await query;

      if (error) {
        console.error("Error fetching requests:", error);
        toast.error("Failed to fetch key requests");
        return;
      }

      let requestsData = (data as any) || [];

      // Pour chaque requête approved, récupérer le statut is_activated depuis la table keys
      requestsData = await Promise.all(
        requestsData.map(async (request: any) => {
          let is_activated: boolean | undefined = undefined;

          if (request.status === 'approved' && request.response_message) {
            // Extraire le key_code du message "Key assigned: XXXXX-XXXXX-XXXXX"
            const keyCode = request.response_message.replace('Key assigned: ', '').trim();

            if (keyCode) {
              const { data: keyDataArray, error } = await supabase
                .from('keys')
                .select('is_activated')
                .eq('key_code', keyCode)
                .eq('claimed_by', request.creator_id)
                .limit(1);

              const keyData = keyDataArray?.[0];


              // Explicitly set the value - if keyData exists, use is_activated (even if false)
              if (keyData !== null && keyData !== undefined) {
                is_activated = keyData.is_activated;
              }
            }
          }

          return {
            ...request,
            is_activated
          };
        })
      );

      // Load report information for each request
      if (requestsData && user?.id && userType === 'developer') {
        const requestsWithReportInfo = await Promise.all(
          requestsData.map(async (request: any) => {
            const hasReported = await hasDeveloperReportedCreator(user.id, request.creator.id);
            const reportDetails = await getCreatorReportDetails(request.creator.id);
            const trustScore = await getCreatorTrustScore(request.creator.id);

            return {
              ...request,
              creator: {
                ...request.creator,
                steam_url: request.creator.steam_url || null,
                steam_id: request.creator.steam_id || null,
                is_verified: request.creator.is_verified || false,
                has_reported: hasReported,
                report_details: reportDetails,
                trust_score: trustScore
              }
            };
          })
        );

        setRequests(requestsWithReportInfo);
      } else {
        setRequests(requestsData);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    try {
      const { error } = await supabase
        .from("key_requests")
        .delete()
        .eq("id", requestId);

      if (error) {
        console.error("Error canceling request:", error);
        toast.error("Failed to cancel request");
        return;
      }

      toast.success("Request canceled successfully");
      fetchRequests(); // Refresh the list
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred");
    }
  };

  const handleReportCreator = async (creatorId: string, isReporting: boolean) => {
    if (!user?.id) return;

    try {
      if (isReporting) {
        await addReport(user.id, creatorId);
        toast.success('Creator reported successfully');
      } else {
        await removeReport(user.id, creatorId);
        toast.success('Creator unreported successfully');
      }
      // Refresh the requests to show updated data
      await fetchRequests();
    } catch (error) {
      console.error('Error reporting creator:', error);
      toast.error('Failed to report creator');
    }
  };

  const handleAcceptRequest = async (requestId: string) => {
    await handleStatusChange(requestId, 'approved');
  };

  const handleRejectRequest = async (requestId: string) => {
    await handleStatusChange(requestId, 'rejected');
  };

  const handleStatusChange = async (requestId: string, newStatus: 'approved' | 'rejected') => {
    try {
      if (newStatus === 'approved') {
        // Trouver la requête pour obtenir les informations nécessaires
        const request = requests.find(r => r.id === requestId);
        if (!request) {
          toast.error("Request not found");
          return;
        }

        // Déterminer le type de clé à utiliser selon le user_type du requester
        const { data: requesterProfile } = await supabase
          .from("profiles")
          .select("user_type")
          .eq("id", request.creator_id)
          .single();

        const isPlayer = (requesterProfile as any)?.user_type === 'player';

        // Trouver une clé disponible pour ce jeu (publique pour players, créateur pour creators)
        const keysQuery = supabase
          .from("keys")
          .select("id, key_code")
          .eq("game_id", request.game_id)
          .eq("is_claimed", false)
          .eq("public", isPlayer ? true : false)
          .limit(1);

        const { data: availableKeys, error: keysError } = await keysQuery;

        if (keysError) {
          console.error("Error fetching available keys:", keysError);
          toast.error("Failed to fetch available keys");
          return;
        }

        if (!availableKeys || availableKeys.length === 0) {
          toast.error("No available keys for this game");
          return;
        }

        const selectedKey = availableKeys[0];

        // Marquer la clé comme réclamée
        const { error: claimError } = await supabase
          .from("keys")
          .update({
            is_claimed: true,
            claimed_by: request.creator_id,
            claimed_at: new Date().toISOString()
          })
          .eq("id", selectedKey.id);

        if (claimError) {
          console.error("Error claiming key:", claimError);
          toast.error("Failed to claim key");
          return;
        }

        // Mettre à jour le statut de la requête
        const { error: updateError } = await supabase
          .from("key_requests")
          .update({
            status: newStatus,
            response_message: `Key assigned: ${selectedKey.key_code}`
          })
          .eq("id", requestId);

        if (updateError) {
          console.error("Error updating request:", updateError);
          toast.error("Failed to update request");
          return;
        }

        // Décrémenter le compteur available_keys dans la table games
        // D'abord, récupérer la valeur actuelle
        const { data: currentGame, error: fetchError } = await supabase
          .from("games")
          .select("available_keys")
          .eq("id", request.game_id)
          .single();

        if (fetchError) {
          console.error("Error fetching current available_keys:", fetchError);
        } else if (currentGame && currentGame.available_keys > 0) {
          const { error: updateGameError } = await supabase
            .from("games")
            .update({
              available_keys: currentGame.available_keys - 1
            })
            .eq("id", request.game_id);

          if (updateGameError) {
            console.error("Error updating game available_keys:", updateGameError);
            // Ne pas faire échouer l'opération pour ça, juste logger
          }
        }

        toast.success("Request approved and key assigned!");

        // Recalculate creator badges (in background)
        if (request.creator_id) {
          recalculateCreatorBadges(request.creator_id).catch(error => {
            console.error('Error recalculating creator badges:', error);
          });
        }
      } else {
        // Pour les requêtes déclinées, juste mettre à jour le statut
        const { error } = await supabase
          .from("key_requests")
          .update({ status: newStatus })
          .eq("id", requestId);

        if (error) {
          console.error("Error updating request:", error);
          toast.error("Failed to update request");
          return;
        }

        toast.success("Request rejected");
      }

      fetchRequests(); // Refresh the list
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred");
    }
  };

  const handleGameClick = (gameId: string) => {
    navigate(`/game/${gameId}`);
  };

  const handleCreatorClick = (creator: any) => {
    setSelectedCreator(creator);
    setIsViewingCreatorProfile(true);
  };

  const handleViewTrackingInfo = async (gameId: string, gameKey: string) => {
    try {
      const { data, error } = await supabase
        .from("games")
        .select(`
          id,
          title,
          hashtag,
          youtube_tag,
          twitch_category,
          keymailer_hashtag,
          disclosure_text,
          developer:profiles!games_developer_id_fkey(
            id,
            display_name,
            studio_name,
            studio_logo_url,
            studio_slug
          )
        `)
        .eq("id", gameId)
        .single();

      if (error || !data) {
        console.error("Error fetching game details:", error);
        toast.error("Failed to load game information");
        return;
      }

      const gameData = data as any;
      setSelectedGameForTracking({
        id: gameData.id,
        title: gameData.title,
        hashtag: gameData.hashtag,
        youtube_tag: gameData.youtube_tag,
        twitch_category: gameData.twitch_category,
        keymailer_hashtag: gameData.keymailer_hashtag,
        disclosure_text: gameData.disclosure_text,
        developer: gameData.developer,
        key: gameKey
      });
      setIsViewingTrackingInfo(true);
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred");
    }
  };


  // Show loading state while checking verification
  if (isCheckingVerification) {
    return (
      <Layout>
        <SEO
          title="Key Requests"
          description="Manage your key requests and distribution on IndieKeys."
        />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </Layout>
    );
  }

  // Show restriction message if developer is not verified
  if (userType === 'developer' && isVerified !== true) {
    return (
      <Layout>
        <SEO
          title="Key Requests"
          description="Manage your key requests and distribution on IndieKeys."
        />
        <AccountNotVerified />
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title="Key Requests"
        description="Manage your key requests and distribution on IndieKeys."
      />
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <KeyRequestsList
            requests={requests}
            onViewTrackingInfo={handleViewTrackingInfo}
            onCancelRequest={handleCancelRequest}
            onAcceptRequest={userType === 'developer' ? handleAcceptRequest : undefined}
            onRejectRequest={userType === 'developer' ? handleRejectRequest : undefined}
            onReportCreator={userType === 'developer' ? handleReportCreator : undefined}
            onCreatorClick={userType === 'developer' ? handleCreatorClick : undefined}
            showCancelButton={true}
            userType={userType as 'creator' | 'developer' | null}
            hideTitle={false}
            formatReportDate={formatReportDate}
            getTrustScoreColor={getTrustScoreColor}
            getTrustScoreEmoji={getTrustScoreEmoji}
            getTrustScoreLabel={getTrustScoreLabel}
            initialGameFilter={gameIdFromUrl || undefined}
          />
        )}
      </div>

      {/* Creator Profile Modal */}
      <CreatorProfileModal
        isOpen={isViewingCreatorProfile}
        onClose={() => setIsViewingCreatorProfile(false)}
        creator={selectedCreator}
        userType={userType}
        onCreatorUpdate={fetchRequests}
      />

      {/* Tracking Info Modal */}
      {selectedGameForTracking && (
        <TrackingInfoModal
          isOpen={isViewingTrackingInfo}
          onClose={() => setIsViewingTrackingInfo(false)}
          game={selectedGameForTracking}
          gameKey={selectedGameForTracking.key}
          userType={userType}
          onStartChat={() => {
            setIsViewingTrackingInfo(false);
            setIsViewingChat(true);
          }}
        />
      )}

      {/* Game Chat Modal */}
      {selectedGameForTracking && selectedGameForTracking.developer && (
        <GameChatModal
          isOpen={isViewingChat}
          onClose={() => setIsViewingChat(false)}
          game={selectedGameForTracking}
          developer={selectedGameForTracking.developer}
          currentUserId={user?.id || ''}
        />
      )}
    </Layout>
  );
}
